
import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Marketplace from './pages/Marketplace';
import ToolDetail from './pages/ToolDetail';
import Pricing from './pages/Pricing';
import Settings from './pages/Settings';
import Automations from './pages/Automations';
import { UserProfile, UserRole, PlanType, ToolRun, AppearanceMode } from './types';
import { AI_TOOLS } from './constants';

const TOOL_ID_ALIASES = AI_TOOLS.reduce((map, tool) => {
  map.set(tool.id, tool.id);
  if (tool.catalogId) map.set(tool.catalogId, tool.id);
  if (tool.commandSlug) map.set(tool.commandSlug, tool.id);
  return map;
}, new Map<string, string>());

const normalizeToolIds = (toolIds: string[]) =>
  toolIds.map((toolId) => TOOL_ID_ALIASES.get(toolId) || toolId);

const normalizePlan = (plan: unknown): PlanType => {
  const value = String(plan || '').toLowerCase().replace(/[\s_-]+/g, '');
  if (value === 'pro+' || value === 'proplus' || value === 'enterprise' || value === 'superior' || value === 'premium') {
    return PlanType.PRO_PLUS;
  }
  if (value === 'pro' || value === 'standard') {
    return PlanType.PRO;
  }
  return PlanType.FREE;
};

const App: React.FC = () => {
  const [user, setUser] = useState<UserProfile>(() => {
    const saved = localStorage.getItem('mx_user_profile');
    if (saved) {
      const parsed = JSON.parse(saved) as UserProfile;
      return {
        ...parsed,
        plan: normalizePlan(parsed.plan),
        activeToolIds: normalizeToolIds(parsed.activeToolIds || []),
        pinnedToolIds: normalizeToolIds(parsed.pinnedToolIds || []),
      };
    }

    return {
      id: 'user-001',
      name: 'Alex Sterling',
      email: 'alex@sterling-ai.com',
      role: UserRole.AGENCY_CLIENT,
      plan: PlanType.PRO,
      creditsRemaining: 18450,
      storageUsedGb: 4.2,
      activeToolIds: ['seo_keyword_intent_classifier', 'data_cleanup_tool', 'linkedin_company_preview', 'email_personalization_snippets'],
      pinnedToolIds: ['mr2'],
      appearanceMode: AppearanceMode.DARK
    };
  });

  const [runs, setRuns] = useState<ToolRun[]>([]);

  useEffect(() => {
    localStorage.setItem('mx_user_profile', JSON.stringify(user));
  }, [user]);

  const handleUpdateUser = (updates: Partial<UserProfile>) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const handleAddRun = (newRun: ToolRun) => {
    setRuns(prev => [newRun, ...prev]);
    setUser(prev => ({
      ...prev,
      creditsRemaining: prev.creditsRemaining - newRun.creditsSpent
    }));
  };

  const handleEnableTool = (toolId: string) => {
    if (!user.activeToolIds.includes(toolId)) {
      setUser(prev => ({
        ...prev,
        activeToolIds: [...prev.activeToolIds, toolId]
      }));
    }
  };

  const handleDisableTool = (toolId: string) => {
    setUser(prev => ({
      ...prev,
      activeToolIds: prev.activeToolIds.filter(id => id !== toolId)
    }));
  };


  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<Navigate to="/app/dashboard" replace />} />

        <Route path="/app/*" element={
          <Layout user={user} onUpdateUser={handleUpdateUser}>
            <Routes>
              <Route path="dashboard" element={<Dashboard user={user} recentRuns={runs} />} />
              <Route path="tools" element={<Marketplace onEnableTool={handleEnableTool} onDisableTool={handleDisableTool} activeToolIds={user.activeToolIds} />} />
              <Route path="tools/:toolId" element={<ToolDetail onAddRun={handleAddRun} credits={user.creditsRemaining} userPlan={user.plan} />} />
              <Route path="settings" element={<Settings user={user} onUpdateUser={handleUpdateUser} />} />
              <Route path="automations" element={<Automations activeToolIds={user.activeToolIds} userPlan={user.plan} />} />
              <Route path="history" element={
                <div className="space-y-6">
                  <h2 className="text-xl font-bold">Full Run History</h2>
                  <div className="space-y-2">
                    {runs.map(run => (
                      <div key={run.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-xl flex items-center justify-between shadow-sm transition-colors">
                        <div className="font-bold text-sm uppercase tracking-tighter">{run.toolId}</div>
                        <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{new Date(run.timestamp).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                  {runs.length === 0 && <p className="text-slate-500 py-20 text-center italic">No runs recorded yet.</p>}
                </div>
              } />
              <Route path="billing" element={<Pricing />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </Layout>
        } />
      </Routes>
    </HashRouter>
  );
};

export default App;
